import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApprovalState, IApprovalFlowDto, ReportType } from '@fdm/api-client';
import { ReportFormStore } from './report-form.store';
import { CurrentUserService } from '@fdm/services';

export enum FieldLocation {
  GeneralTab = 'general',
  ApprovalFlowTab = 'approvalFlow',
  Both = 'both'
}

export interface HighlightedField {
  fieldName: string;
  location: FieldLocation;
  role: string;
}

@Injectable({
  providedIn: 'root',
})
export class FieldHighlightService {
  private _store = inject(ReportFormStore);
  private _userService = inject(CurrentUserService);

  general = toSignal(this._store.general$);
  approvalFlow = toSignal(this._store.approvalFlow$);
  reportType = this._store.reportType;
  abcPrioritization = toSignal(this._store.abcPrioritization$);
  criticality = toSignal(this._store.criticality$);
  authLevel = toSignal(this._store.assignedManagementAuthorizationLevel$);

  currentApprovalState = computed(() => this.general()?.currentState);

  /**
   * Returns the field that should be highlighted based on the current approval state
   */
  highlightedField = computed((): HighlightedField | null => {
    const state = this.currentApprovalState();
    const reportType = this.reportType();
    const abcPrioritization = this.abcPrioritization();
    const criticality = this.criticality();
    const authLevel = this.authLevel();
    const flowState = this.approvalFlow();
    const approvalFlowData = this.approvalFlow();

    if (!state || !reportType) {
      return null;
    }

    return this._determineHighlightedField(
      state,
      reportType,
      abcPrioritization,
      criticality,
      authLevel,
      flowState,
      approvalFlowData
    );
  });

  private _determineHighlightedField(
    state: ApprovalState,
    reportType: ReportType,
    abcPrioritization?: string,
    criticality?: string | null,
    authLevel?: string | null,
    flowState?: IApprovalFlowDto | null,
    approvalFlowData?: IApprovalFlowDto | null
  ): HighlightedField | null {
    const isHCE = criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality);
    const endorsers = this.endorsers();

    switch (state) {
      case ApprovalState.Draft:
      case ApprovalState.Recycle:
        return {
          fieldName: 'initiator',
          location: FieldLocation.GeneralTab,
          role: 'Initiator'
        };

      case ApprovalState.DraftedForReview:
        if (reportType === ReportType.AnomalyReport) {
          return {
            fieldName: 'areaLeadInspEng',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Lead Insp/Eng'
          };
        }

        if (
          reportType === ReportType.IntegrityThreat ||
          reportType === ReportType.ReliabilityThreat ||
          reportType === ReportType.ReliabilityDeficiency ||
          reportType === ReportType.IntegrityDeficiency ||
          reportType === ReportType.TaskDeferral
        ) {
          if (approvalFlowData?.areaLeadInspEng && !flowState?.hasAreaLeadApproved) {
            return {
              fieldName: 'areaLeadInspEng',
              location: FieldLocation.ApprovalFlowTab,
              role: 'Area Lead Insp/Eng'
            };
          } else {
            return {
              fieldName: 'inspEngSupervisor',
              location: FieldLocation.ApprovalFlowTab,
              role: 'Insp Eng Supervisor'
            };
          }
        }
        break;

      case ApprovalState.SubmittedToOpsAdvisor:
        if (
          reportType === ReportType.IntegrityThreat ||
          reportType === ReportType.ReliabilityThreat ||
          reportType === ReportType.ReliabilityDeficiency ||
          reportType === ReportType.IntegrityDeficiency
        ) {
          return {
            fieldName: 'srOpsConsultant',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Operations Advisor'
          };
        }
        if (reportType === ReportType.TaskDeferral) {
          return {
            fieldName: 'srOpsConsultant',
            location: FieldLocation.ApprovalFlowTab,
            // role: 'RIPT/Risk facilitators'
            role: 'Operations Advisor'
          };
        }
        break;

      case ApprovalState.PlanConfirmed:
        if (reportType === ReportType.AnomalyReport) {
          return {
            fieldName: 'inspEngSupervisor',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Insp Eng Supervisor'
          };
        }

        if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat || reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency) {
          return {
            fieldName: '',
            location: FieldLocation.Both,
            role: 'RIPT/Risk facilitators'
          };
        }

        if (reportType === ReportType.TaskDeferral) {
          if (isHCE) {
            // define endorser highlight logics here
            // Asset type HCEPO
            if (!flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
              return {
                fieldName: 'assetTypeHCEPO',
                location: FieldLocation.ApprovalFlowTab,
                role: 'Asset Type HCEPO'
              };
            }
            // Endorser 1
            else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              return {
                fieldName: 'additionalEndorser1',
                location: FieldLocation.ApprovalFlowTab,
                role: 'additionalEndorser1'
              };
            }
            // Endorser 2
            else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
              return {
                fieldName: 'additionalEndorser2',
                location: FieldLocation.ApprovalFlowTab,
                role: 'additionalEndorser2'
              };
            }
            // Endorser 3
            else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
              return {
                fieldName: 'additionalEndorser3',
                location: FieldLocation.ApprovalFlowTab,
                role: 'additionalEndorser3'
              };
            }
          } else {
            if (!flowState?.hasAreaOpsApproved) {
              return {
                fieldName: 'areaOpsSuperintendent',
                location: FieldLocation.ApprovalFlowTab,
                role: 'Area Ops Superintendent'
              };
            }
          }
        }
        break;

      case ApprovalState.RiskEvaluationScored:
        if (
          reportType === ReportType.ReliabilityDeficiency ||
          reportType === ReportType.IntegrityDeficiency ||
          reportType === ReportType.TaskDeferral
        ) {
          return {
            fieldName: 'srOpsConsultant',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Operations Advisor'
          };
        }

        break;

      case ApprovalState.RIPTScored:
        return this._handleRIPTScored(reportType, abcPrioritization, criticality, flowState);

      case ApprovalState.PlanEndorsed:
        return this._handlePlanEndorsed(reportType, criticality, authLevel, flowState, approvalFlowData);

      case ApprovalState.PlanApproved:
        return {
          fieldName: '',
          location: FieldLocation.Both,
          role: 'Action Item to be Completed'
        };

      case ApprovalState.ResolutionPlanCompleted:
        return reportType === ReportType.TaskDeferral
          ? {
            fieldName: 'owner',
            location: FieldLocation.GeneralTab,
            role: 'Owner'
          }
          : {
            fieldName: 'initiator',
            location: FieldLocation.GeneralTab,
            role: 'Initiator'
          };

      case ApprovalState.ValidationAndVerificationCompleted:


        if (
          reportType === ReportType.IntegrityThreat ||
          reportType === ReportType.ReliabilityThreat ||
          reportType === ReportType.ReliabilityDeficiency ||
          reportType === ReportType.IntegrityDeficiency ||
          reportType === ReportType.TaskDeferral         

        ) {
          return {
            fieldName: 'owner',
            location: FieldLocation.GeneralTab,
            role: 'Owner'
          };
        } else if (reportType === ReportType.AnomalyReport) {
          return {
            fieldName: 'initiator',
            location: FieldLocation.GeneralTab,
            role: 'Initiator'
          };
        }
        break;
    }

    return null;
  }

  private _handleRIPTScored(
    reportType: ReportType,
    abcPrioritization?: string,
    criticality?: string | null,
    flowState?: any
  ): HighlightedField | null {
    const isHCE = criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality);
    const endorsers = this.endorsers();

    if (reportType === ReportType.AnomalyReport) {
      return {
        fieldName: 'inspEngSupervisor',
        location: FieldLocation.ApprovalFlowTab,
        role: 'Insp Eng Supervisor'
      };
    }

    if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
      if ((isHCE && abcPrioritization === 'A')) {
        // define endorser highlight logics here
        // Asset type HCEPO
        if (!flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
          return {
            fieldName: 'assetTypeHCEPO',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Asset Type HCEPO'
          };
        }
        // Endorser 1
        else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
          return {
            fieldName: 'additionalEndorser1',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser1'
          };
        }
        // Endorser 2
        else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
          return {
            fieldName: 'additionalEndorser2',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser2'
          };
        }
        // Endorser 3
        else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
          return {
            fieldName: 'additionalEndorser3',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser3'
          };
        }
        // Area Ops Superintendent endorses after HCEPO (and endorsers) have endorsed
        else if (flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        }
      } else {
        if (!flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        }
      }
    }

    if (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
       if (criticality === 'IC3') {
         // define endorser highlight logics here
         // Asset type HCEPO
         if (!flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
           return {
             fieldName: 'assetTypeHCEPO',
             location: FieldLocation.ApprovalFlowTab,
             role: 'Asset Type HCEPO'
           };
         }
         // Endorser 1
         else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
           return {
             fieldName: 'additionalEndorser1',
             location: FieldLocation.ApprovalFlowTab,
             role: 'additionalEndorser1'
           };
         }
         // Endorser 2
         else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
           return {
             fieldName: 'additionalEndorser2',
             location: FieldLocation.ApprovalFlowTab,
             role: 'additionalEndorser2'
           };
         }
         // Endorser 3
         else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
           return {
             fieldName: 'additionalEndorser3',
             location: FieldLocation.ApprovalFlowTab,
             role: 'additionalEndorser3'
           };
         }
         // Area Ops Superintendent endorses after HCEPO has endorsed
         else if (flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
           return {
             fieldName: 'areaOpsSuperintendent',
             location: FieldLocation.ApprovalFlowTab,
             role: 'Area Ops Superintendent'
           };
         }

         return null;
       }


      if ((criticality === 'IC2' || criticality === 'IC1' || criticality === 'AC1' || criticality === 'AC2') && !flowState?.hasLTAApproved) {
        return {
          fieldName: 'localTechnicalAuthority',
          location: FieldLocation.ApprovalFlowTab,
          role: 'Local Technical Authority'
        };
      }
      if (!isHCE && !flowState?.hasAreaOpsApproved) {
        return {
          fieldName: 'areaOpsSuperintendent',
          location: FieldLocation.ApprovalFlowTab,
          role: 'Area Ops Superintendent'
        };
      }

      return {
        fieldName: 'areaOpsSuperintendent',
        location: FieldLocation.ApprovalFlowTab,
        role: 'Area Ops Superintendent'
      };
    }



    return null;
  }

  endorsers = computed(() => {
    const authorizedUserEmail = this._userService.email();
    const approvalState = this.approvalFlow();

    const endorsers = [
      {
        email: approvalState?.additionalEndorser1,
        endorsed: approvalState?.additionalEndorser1Endorsed,
      },
      {
        email: approvalState?.additionalEndorser2,
        endorsed: approvalState?.additionalEndorser2Endorsed,
      },
      {
        email: approvalState?.additionalEndorser3,
        endorsed: approvalState?.additionalEndorser3Endorsed,
      },
    ];

    const haveEndorsers = endorsers.some(item => item.email != undefined);
    const activeEndorsers = endorsers.filter(item => item.email != undefined);
    const allApproved = activeEndorsers.every(item => item.endorsed);
    const isEndorser1Active = !endorsers[0].endorsed; // && authorizedUserEmail === endorsers[0].email;
    const isEndorser2Active = endorsers[0].endorsed && !endorsers[1].endorsed; // && authorizedUserEmail === endorsers[1].email;
    // const isEndorser2Active = true
    const isEndorser3Active = endorsers[1].endorsed && !endorsers[2].endorsed; // && authorizedUserEmail === endorsers[2].email;
    const isEndorserActive = haveEndorsers && (isEndorser1Active || isEndorser2Active || isEndorser3Active);

    return { haveEndorsers, allApproved, isEndorserActive, isEndorser1Active, isEndorser2Active, isEndorser3Active };
  });

  private _handlePlanEndorsed(
    reportType: ReportType,
    criticality?: string | null,
    authLevel?: string | null,
    flowState?: any,
    approvalFlowData?: IApprovalFlowDto | null
  ): HighlightedField | null {
    const endorsers = this.endorsers();

    if (reportType === ReportType.TaskDeferral) {
      // define endorser highlight logics here
      // Asset type HCEPO
      if (!flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
        return {
          fieldName: 'assetTypeHCEPO',
          location: FieldLocation.ApprovalFlowTab,
          role: 'Asset Type HCEPO'
        };
      }
      // Endorser 1
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
        return {
          fieldName: 'additionalEndorser1',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser1'
        };
      }
      // Endorser 2
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
        return {
          fieldName: 'additionalEndorser2',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser2'
        };
      }
      // Endorser 3
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
        return {
          fieldName: 'additionalEndorser3',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser3'
        };
      }

      if (authLevel === 'A') {
        if (flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        }
      }

      if (authLevel === 'B' || authLevel === 'C' || authLevel === 'D') {
        if (flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        } else if (flowState?.hasHCEPOApproved && flowState?.hasAreaOpsApproved && !flowState?.hasProdOpManagerApproved) {
          return {
            fieldName: 'productionOperationManager',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Production Operation Manager'
          };
        }
      }

      if (authLevel === 'C' || authLevel === 'D') {
        if (
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          flowState?.hasProdOpManagerApproved &&
          !flowState?.hasGenManagerOperatorApproved
        ) {
          return {
            fieldName: 'generalManagerOperation',
            location: FieldLocation.ApprovalFlowTab,
            role: 'General Manager Operation'
          };
        }
      }

      if (authLevel === 'D') {
        if (
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          flowState?.hasProdOpManagerApproved &&
          flowState?.hasGenManagerOperatorApproved &&
          !flowState?.hasDirectorApproved
        ) {
          return {
            fieldName: 'generalDirectorOperation',
            location: FieldLocation.ApprovalFlowTab,
            role: 'General Director'
          };
        }
      }
    }

    if (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
      if (criticality === 'IC3') {
        // Asset type HCEPO
        if (!flowState?.hasHCEPOApproved && endorsers.allApproved) {
          return {
            fieldName: 'assetTypeHCEPO',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Asset Type HCEPO'
          };
        }
        // Endorser 1
        else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
          return {
            fieldName: 'additionalEndorser1',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser1'
          };
        }
        // Endorser 2
        else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
          return {
            fieldName: 'additionalEndorser2',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser2'
          };
        }
        // Endorser 3
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
          return {
            fieldName: 'additionalEndorser3',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser3'
          };
        }
        else if (flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        } else if (flowState?.hasHCEPOApproved && flowState?.hasAreaOpsApproved && !flowState?.hasProdOpManagerApproved) {
          return {
            fieldName: 'productionOperationManager',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Production Operation Manager'
          };
        }
      } else if (criticality === 'IC2' || criticality === 'AC2') {
        // if (!flowState?.hasLTAApproved) {
        //   return {
        //     fieldName: 'localTechnicalAuthority',
        //     location: FieldLocation.ApprovalFlowTab,
        //     role: 'Local Technical Authority'
        //   };
        // }
        // define endorser highlight logics here
        // Asset type HCEPO
        if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
          return {
            fieldName: 'assetTypeHCEPO',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Asset Type HCEPO'
          };
        }
        // Endorser 1
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
          return {
            fieldName: 'additionalEndorser1',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser1'
          };
        }
        // Endorser 2
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
          return {
            fieldName: 'additionalEndorser2',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser2'
          };
        }
        // Endorser 3
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
          return {
            fieldName: 'additionalEndorser3',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser3'
          };
        }


        // LTA
        else if (flowState?.hasLTAApproved && flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        } else if (
          flowState?.hasLTAApproved &&
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          !flowState?.hasProdOpManagerApproved
        ) {
          return {
            fieldName: 'productionOperationManager',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Production Operation Manager'
          };
        } else if (
          flowState?.hasLTAApproved &&
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          flowState?.hasProdOpManagerApproved &&
          !flowState?.hasGenManagerOperatorApproved
        ) {
          return {
            fieldName: 'generalManagerOperation',
            location: FieldLocation.ApprovalFlowTab,
            role: 'General Manager Operation'
          };
        }
      } else if (criticality === 'IC1' || criticality === 'AC1') {
        // if (!flowState?.hasLTAApproved) {
        //   return {
        //     fieldName: 'localTechnicalAuthority',
        //     location: FieldLocation.ApprovalFlowTab,
        //     role: 'Local Technical Authority'
        //   };
        // }
        // Asset type HCEPO
        if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
          return {
            fieldName: 'assetTypeHCEPO',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Asset Type HCEPO'
          };
        }
        // Endorser 1
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
          return {
            fieldName: 'additionalEndorser1',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser1'
          };
        }
        // Endorser 2
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
          return {
            fieldName: 'additionalEndorser2',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser2'
          };
        }
        // Endorser 3
        else if (flowState?.hasLTAApproved && !flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
          return {
            fieldName: 'additionalEndorser3',
            location: FieldLocation.ApprovalFlowTab,
            role: 'additionalEndorser3'
          };
        }
        else if (flowState?.hasLTAApproved && flowState?.hasHCEPOApproved && !flowState?.hasAreaOpsApproved) {
          return {
            fieldName: 'areaOpsSuperintendent',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Area Ops Superintendent'
          };
        } else if (
          flowState?.hasLTAApproved &&
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          !flowState?.hasProdOpManagerApproved
        ) {
          return {
            fieldName: 'productionOperationManager',
            location: FieldLocation.ApprovalFlowTab,
            role: 'Production Operation Manager'
          };
        } else if (
          flowState?.hasLTAApproved &&
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          flowState?.hasProdOpManagerApproved &&
          !flowState?.hasGenManagerOperatorApproved
        ) {
          return {
            fieldName: 'generalManagerOperation',
            location: FieldLocation.ApprovalFlowTab,
            role: 'General Manager Operation'
          };
        } else if (
          flowState?.hasLTAApproved &&
          flowState?.hasHCEPOApproved &&
          flowState?.hasAreaOpsApproved &&
          flowState?.hasProdOpManagerApproved &&
          flowState?.hasGenManagerOperatorApproved &&
          !flowState?.hasDirectorApproved
        ) {
          return {
            fieldName: 'generalDirectorOperation',
            location: FieldLocation.ApprovalFlowTab,
            role: 'General Director'
          };
        }
      }
    }

    if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
      // Asset type HCEPO
      if (!flowState?.hasHCEPOApproved && endorsers.allApproved) {
        return {
          fieldName: 'assetTypeHCEPO',
          location: FieldLocation.ApprovalFlowTab,
          role: 'Asset Type HCEPO'
        };
      }
      // Endorser 1
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser1Active) {
        return {
          fieldName: 'additionalEndorser1',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser1'
        };
      }
      // Endorser 2
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser2Active) {
        return {
          fieldName: 'additionalEndorser2',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser2'
        };
      }
      // Endorser 3
      else if (!flowState?.hasHCEPOApproved && endorsers.isEndorser3Active) {
        return {
          fieldName: 'additionalEndorser3',
          location: FieldLocation.ApprovalFlowTab,
          role: 'additionalEndorser3'
        };
      } else {
        return {
          fieldName: 'areaOpsSuperintendent',
          location: FieldLocation.ApprovalFlowTab,
          role: 'Area Ops Superintendent'
        };
      }

    }

    return null;
  }

  // field highlight checking
  isFieldHighlighted(fieldName: string, location: FieldLocation): boolean {
    const highlighted = this.highlightedField();
    return highlighted?.fieldName === fieldName && highlighted?.location === location;
  }


  //  CSS classes for highlighted field
  getHighlightClasses(fieldName: string, location: FieldLocation): string[] {
    if (this.isFieldHighlighted(fieldName, location)) {
      return ['field-highlighted', 'field-highlighted-orange'];
    }
    return [];
  }
}
